import React from 'react';
import Form from 'components/form';
import PSPDFKitWeb from 'pspdfkit';
import Annotation, {
  GlobalLinkCounter,
} from 'components/pspdf/components/simple-custom-annotations/components/annotation';
import useSurfaceNodeInitialValuesFromServer from 'routes/surface/components/node-modal-form/hooks/use-surface-node-initial-values-from-server';
import useCanCreateOrEditSurfaceNode from 'routes/surface/components/node-modal-form/hooks/use-can-create-or-edit-surface-node';
import useSurfaceNodeTextSnippets from 'routes/surface/hooks/use-surface-node-text-snippets';
import useProxyUrl from 'hooks/use-proxy-url';
import useQuerySearchParams from 'routes/surface/hooks/use-query-search-params';
import { useGlobalLinks } from 'state/canvas/node-linking/global-links';
import { useTranslation } from 'i18n';
import Fullscreen, {
  BackButton,
  Header,
  HeaderControls,
  NameTextInput,
} from './style';
import { WithMargin } from 'components/layout';
import { MarkupMenu } from 'components/pspdf/components/markup-menu';
import { useHistory } from 'react-router-dom';
import { mixed, object, string } from 'yup';
import {
  SurfaceNodeType,
  SnippetOperation,
  SurfaceDocumentNode,
} from 'graphql/types';
import { NodeModalInput } from 'types';
import { isMobile } from 'react-device-detect';

import { renderToString } from 'react-dom/server';

import { ServerStyleSheet } from 'styled-components';
import { useFormikContext } from 'formik';

const sheet = new ServerStyleSheet();

export interface DocumentNodeFormProps {
  onSubmit: any;
  closeForm: Function;
  initialValues: NodeModalInput<SurfaceDocumentNode>;
  backButton: Function;
}

const defaultProps = {
  backButton: () => null,
};

function createValidationSchema(t, isFileRequired) {
  return function () {
    return object().shape({
      name: string().required(t('name is required')),
      //url: string().required(t("url is required"))
      ...(isFileRequired
        ? { file: mixed().required(t('file is required')) }
        : {}),
    });
  };
}

function DocumentNodeForm(props: DocumentNodeFormProps) {
  const { t } = useTranslation();

  const { initialValues, isLoading } = useSurfaceNodeInitialValuesFromServer(
    props.initialValues,
  );

  const { canCreateOrEdit, isUpdate } = useCanCreateOrEditSurfaceNode(
    initialValues,
  );

  const [[globalLink]] = useGlobalLinks();

  const history = useHistory();

  const searchParams = useQuerySearchParams();

  const form = useFormikContext();

  const {
    snippets,
    onTextSnippetSave,
    onTextSnippetDelete,
    onStartLinking,
    onFinishLinking,
    onDeleteLink,
    onGlobalLinkIndicatorClick,
    shouldDeleteSnippet,
    shouldDeleteLinkSnippet,
    shouldDeleteNamedSnippet,
    shouldShowDeleteLinkAction,
    showFinishLinkAction,
    removeDeletedSnippetFromGlobalLinkZone,
  } = useSurfaceNodeTextSnippets(
    initialValues.snippets,
    onSubmit,
    props.closeForm,
  );

  const submitLabel = initialValues.id
    ? t('Save document')
    : t('Insert document');

  const isFileRequired = !isUpdate && !initialValues.fromRis;

  const validationSchema = React.useMemo(
    createValidationSchema(t, isFileRequired),
    [],
  );

  function isFromRis(url): boolean {
    return url?.includes('ris.bka.gv.at');
  }

  function onSubmit(values, actions, { closeTheForm = true } = {}) {
    const documentSnippets = Array.from(snippets.values());

    const formData = {
      ...values,
      snippets: documentSnippets,
    };

    return props.onSubmit(formData, actions).then((response) => {
      if (response.isSuccess) {
        removeDeletedSnippetFromGlobalLinkZone(documentSnippets);

        closeTheForm && props.closeForm();
      }

      return response;
    });
  }

  function customAnnotationRenderer(
    annotation,
    isSelected,
    parentNode,
    selection,
    renderedAt,
  ) {
    const annotationJSON = annotation.toJS();
    const { customData } = annotationJSON;

    const snippet = snippets.get(customData.annotationId);

    const annotationNotCreatedInSameRender =
      renderedAt &&
      customData.renderedAt &&
      renderedAt !== customData.renderedAt;

    if (annotationNotCreatedInSameRender) {
      if (shouldDeleteNamedSnippet(customData, snippet)) {
        selection.updateAnnotation({ isNamed: false, name: undefined });
      }

      if (shouldDeleteLinkSnippet(customData, snippet, globalLink)) {
        selection.updateAnnotation({ isLink: false });
      }

      if (shouldDeleteSnippet(customData, snippet, globalLink)) {
        if (snippets.has(customData.annotationId)) {
          snippets.set(customData.annotationId, {
            ...snippet,
            operation: SnippetOperation.Delete,
          });
        }

        if (selection.hasDecorations() === false) {
          selection.deleteAnnotation();

          return <div></div>;
        }
      }
    }

    console.log('AAAAAA ', selection, parentNode);

    return (
      <Annotation
        isLink={annotationJSON?.customData?.isLink}
        parentNode={parentNode}
        annotation={annotationJSON}
        isSelected={isSelected}
      />
    );
  }

  function markupMenuRenderer(
    selectedAnnotation,
    instance,
    actions,
    showFinishLinkAction,
  ) {
    const { customData = {} } = selectedAnnotation.toJS();
    const showDeleteLinkAction = shouldShowDeleteLinkAction(customData);

    if (canCreateOrEdit) {
      return (
        <MarkupMenu
          annotation={selectedAnnotation}
          instance={instance}
          actions={actions}
          showFinishLinkAction={showFinishLinkAction}
          showDeleteLinkAction={showDeleteLinkAction}
        />
      );
    }

    return <div></div>;
  }

  function isNameInputAutoFocused() {
    if (isUpdate) {
      return isMobile ? false : true;
    }

    return true;
  }

  const proxyUrl = useProxyUrl();
  const isImage = initialValues.type === SurfaceNodeType.Image;

  if (isLoading) {
    return null;
  }

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {(form) => {
        return (
          <Fullscreen.Wrapper>
            <Header>
              {props.backButton(BackButton)}
              <NameTextInput
                name="name"
                placeholder={t('Name of document')}
                autoFocus={isNameInputAutoFocused()}
                noBorder
                secondary
              />
              <HeaderControls>
                <WithMargin right="15px">
                  <Form.Button
                    label={t('Close')}
                    onClick={props.closeForm}
                    secondary
                  />
                </WithMargin>
                {canCreateOrEdit && (
                  <Form.SubmitButton
                    label={submitLabel}
                    onClick={form.handleSubmit}
                  />
                )}
              </HeaderControls>
            </Header>
            <Fullscreen.Content isImage={isImage}>
              <Form.PSPDF
                name="instantJSON"
                // customAnnotationRenderer={customAnnotationRenderer}
                markupMenuRenderer={markupMenuRenderer}
                onTextSnippetSave={onTextSnippetSave}
                onTextSnippetDelete={onTextSnippetDelete}
                onStartLinking={onStartLinking(form)}
                onFinishLinking={onFinishLinking(form)}
                onDeleteLink={onDeleteLink}
                onGlobalLinkIndicatorClick={onGlobalLinkIndicatorClick}
                showFinishLinkAction={showFinishLinkAction}
                onLinksToggle={async (isShown, instance) => {
                  if (isShown) {
                    //
                    const linkSnippets = Array.from(
                      snippets,
                      ([_, value]) => value,
                    );

                    const instantJSON = await instance.exportInstantJSON();

                    linkSnippets?.forEach((snippet) => {
                      const annotationJson = instantJSON.annotations.find(
                        (annotation) =>
                          annotation.customData.annotationId ===
                          snippet.annotationId,
                      );

                      if (annotationJson?.customData?.isLink) {
                        const globalLinkCount = snippet.pointsTo.length;

                        const [x, y, width, height] = annotationJson.bbox;

                        if (globalLinkCount) {
                          const html = renderToString(
                            sheet.collectStyles(
                              <GlobalLinkCounter
                                annotation={annotationJson}
                                count={globalLinkCount}
                              />,
                            ),
                          );

                          const styleTags = sheet.getStyleTags();

                          const node = document.createElement('div');

                          node.innerHTML = '';
                          node.innerHTML = styleTags + html;

                          const element = new PSPDFKitWeb.CustomOverlayItem({
                            id:
                              'global-link-counter-id-' + snippet.annotationId,
                            node,
                            position: new PSPDFKitWeb.Geometry.Point({
                              x: x + width - 20,
                              y: y + height - 2,
                            }),
                            pageIndex: annotationJson.pageIndex,
                          });

                          instance.setCustomOverlayItem(element);
                        }
                      }
                    });
                  } else {
                    try {
                      Array.from(snippets, ([_, value]) => value).forEach(
                        (snippet) => {
                          instance.removeCustomOverlayItem(
                            'global-link-counter-id-' + snippet.annotationId,
                          );
                        },
                      );
                    } catch (error) {
                      // nothing
                    }
                  }
                }}
                onLoad={(instance, instantJSON) => {
                  instance.addEventListener('page.press', (event) => {
                    try {
                      instance.removeCustomOverlayItem(
                        'global-link-counter-id',
                      );
                    } catch (error) {
                      // nothing
                    }
                  });

                  instance.addEventListener('annotations.press', (event) => {
                    const annotationJson = PSPDFKitWeb.Annotations.toSerializableObject(
                      event.annotation,
                    ) as any;

                    if (!annotationJson.isLink) {
                      try {
                        instance.removeCustomOverlayItem(
                          'global-link-counter-id',
                        );
                      } catch (error) {
                        // nothing
                      }
                    }

                    const snippet = snippets.get(
                      annotationJson.customData.annotationId,
                    );

                    const globalLinkCount = snippet?.pointsTo?.length ?? 0;

                    const [x, y, width, height] = annotationJson.bbox;

                    if (globalLinkCount) {
                      const html = renderToString(
                        sheet.collectStyles(
                          <GlobalLinkCounter
                            annotation={annotationJson}
                            count={globalLinkCount}
                          />,
                        ),
                      );

                      const styleTags = sheet.getStyleTags();

                      const node = document.createElement('div');

                      node.innerHTML = '';
                      node.innerHTML = styleTags + html;

                      const element = new PSPDFKitWeb.CustomOverlayItem({
                        id: 'global-link-counter-id',
                        node,
                        position: new PSPDFKitWeb.Geometry.Point({
                          x: x + width - 20,
                          y: y + height - 2,
                        }),
                        pageIndex: annotationJson.pageIndex,
                      });

                      instance.setCustomOverlayItem(element);
                    }
                  });

                  if (searchParams.get('snippet')) {
                    const snippet = form.values.snippets.find(
                      (snippet) => snippet.id === searchParams.get('snippet'),
                    );

                    const annotation = instantJSON?.annotations.find(
                      (annotation) =>
                        annotation.customData.annotationId ===
                        snippet?.annotationId,
                    );

                    if (annotation) {
                      instance
                        .getAnnotations(annotation.pageIndex)
                        .then((annotations) => {
                          const annotationInstance = annotations.find(
                            (annotationInstance) =>
                              annotationInstance.customData.annotationId ===
                              snippet?.annotationId,
                          );

                          if (annotationInstance) {
                            instance.jumpToRect(
                              annotation.pageIndex,
                              annotationInstance.boundingBox,
                            );

                            searchParams.delete('snippet');

                            history.replace({
                              search: searchParams.toString(),
                            });
                          }
                        });
                    }
                  }
                }}
                url={
                  isFromRis(form.values.url)
                    ? proxyUrl.concat(form.values.url)
                    : form.values.url
                }
                instantJSON={form.values.instantJSON}
              />
            </Fullscreen.Content>
          </Fullscreen.Wrapper>
        );
      }}
    </Form>
  );
}

DocumentNodeForm.defaultProps = defaultProps;

export default DocumentNodeForm;
